/**
 * Organization Detail Modal
 * 사업자 상세 정보 모달 (모듈 관리, 내부 메모 포함)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Organization, isOrganizationSuspended, isOrganizationApproved } from '../../models';
import { API_BASE_URL } from '../../../../config/api';
import { SuspendOwnerModal } from './SuspendOwnerModal';

// Memo 인터페이스
interface Memo {
  id: string;
  organization_id: string;
  content: string;
  author_id: string;
  author_name: string;
  parent_id: string | null;
  is_pinned: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  replies: Memo[];
}

interface Module {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category: string;
  is_core: boolean;
  is_enabled: boolean;
  menu_paths: string[];
  display_order: number;
}

interface OrganizationModules {
  organization_id: string;
  core: Module[];
  optional: Module[];
}

// 변경 이력 인터페이스
interface ChangeLog {
  id: string;
  action: string;
  admin_name: string;
  admin_role: string;
  changes: Record<string, { old: string | null; new: string | null }>;
  details: string;
  created_at: string;
}

// 필드 레이블 매핑
const FIELD_LABELS: Record<string, string> = {
  name: '조직명',
  business_registration_number: '사업자등록번호',
  address: '주소',
  phone: '전화번호',
  service_type: '서비스 유형',
  owner_name: '대표자명',
  owner_email: '대표자 이메일',
  owner_phone: '대표자 연락처',
};

// 대시보드 데이터 인터페이스
interface DashboardData {
  subscription: {
    plan_name: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
    days_remaining: number;
    paid_amount: number;
    payment_status: string;
  };
  recent_payments: {
    id: string;
    date: string;
    plan_name: string;
    amount: number;
    period: string;
    status: string;
  }[];
  user_counts: {
    students: number;
    parents: number;
    total: number;
  };
  recent_activity: {
    owner_last_login: string | null;
    today_access_count: number;
    week_access_count: number;
  };
}

interface OrganizationDetailModalProps {
  organization: Organization;
  onClose: () => void;
  onDeleted?: () => void;
  onUpdated?: () => void;
}

export const OrganizationDetailModal: React.FC<
  OrganizationDetailModalProps
> = ({ organization, onClose, onDeleted, onUpdated }) => {
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'memos' | 'subscription' | 'modules' | 'logs' | 'settings'>('info');
  const [modules, setModules] = useState<OrganizationModules | null>(null);
  const [loadingModules, setLoadingModules] = useState(false);
  const [updatingModule, setUpdatingModule] = useState<string | null>(null);

  // 대시보드 데이터 상태
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // 메모 관련 상태
  const [memos, setMemos] = useState<Memo[]>([]);
  const [loadingMemos, setLoadingMemos] = useState(false);
  const [newMemoContent, setNewMemoContent] = useState('');
  const [savingMemo, setSavingMemo] = useState(false);
  const [editingMemoId, setEditingMemoId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');

  // 삭제 관련 상태
  const [confirmName, setConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // 할인율 관련 상태
  const [discountRate, setDiscountRate] = useState<number>(organization.discount_rate || 0);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountSuccess, setDiscountSuccess] = useState(false);

  // 전화번호 편집 관련 상태
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState(organization.phone || organization.owner_phone || '');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneSuccess, setPhoneSuccess] = useState(false);

  // 기본 정보 편집 모드 관련 상태
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({
    name: organization.name || '',
    business_registration_number: organization.business_number || '',
    address: organization.address || '',
    phone: organization.phone || '',
    service_type: organization.service_type || '',
    owner_name: organization.owner_name || '',
    owner_email: organization.owner_email || '',
    owner_phone: organization.owner_phone || '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // 변경 이력 관련 상태
  const [changeLogs, setChangeLogs] = useState<ChangeLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // 사용 중지/재활성화 관련 상태
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);

  // 메모 목록 조회
  const fetchMemos = useCallback(async () => {
    setLoadingMemos(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos/${organization.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('메모 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setMemos(data);
    } catch (error: any) {
      console.error('메모 조회 오류:', error);
    } finally {
      setLoadingMemos(false);
    }
  }, [organization.id]);

  // 메모 작성
  const handleCreateMemo = async () => {
    if (!newMemoContent.trim()) return;

    setSavingMemo(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            organization_id: organization.id,
            content: newMemoContent,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '메모 작성에 실패했습니다.');
      }

      setNewMemoContent('');
      fetchMemos();
    } catch (error: any) {
      console.error('메모 작성 오류:', error);
      alert(error.message);
    } finally {
      setSavingMemo(false);
    }
  };

  // 답글 작성
  const handleCreateReply = async (parentId: string) => {
    if (!replyContent.trim()) return;

    setSavingMemo(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            organization_id: organization.id,
            content: replyContent,
            parent_id: parentId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '답글 작성에 실패했습니다.');
      }

      setReplyingToId(null);
      setReplyContent('');
      fetchMemos();
    } catch (error: any) {
      console.error('답글 작성 오류:', error);
      alert(error.message);
    } finally {
      setSavingMemo(false);
    }
  };

  // 메모 수정
  const handleUpdateMemo = async (memoId: string) => {
    if (!editingContent.trim()) return;

    setSavingMemo(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos/${memoId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: editingContent,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '메모 수정에 실패했습니다.');
      }

      setEditingMemoId(null);
      setEditingContent('');
      fetchMemos();
    } catch (error: any) {
      console.error('메모 수정 오류:', error);
      alert(error.message);
    } finally {
      setSavingMemo(false);
    }
  };

  // 메모 삭제
  const handleDeleteMemo = async (memoId: string) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos/${memoId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '메모 삭제에 실패했습니다.');
      }

      fetchMemos();
    } catch (error: any) {
      console.error('메모 삭제 오류:', error);
      alert(error.message);
    }
  };

  // 메모 고정/해제
  const handleTogglePin = async (memoId: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos/${memoId}/pin`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '고정 상태 변경에 실패했습니다.');
      }

      fetchMemos();
    } catch (error: any) {
      console.error('메모 고정 오류:', error);
      alert(error.message);
    }
  };

  // 시간 포맷팅
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // 대시보드 데이터 조회
  const fetchDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('대시보드 정보를 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setDashboard(result);
    } catch (error: any) {
      console.error('대시보드 조회 오류:', error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  // 모듈 목록 조회
  const fetchModules = async () => {
    setLoadingModules(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/modules/organizations/${organization.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('모듈 목록을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setModules(result.data);
    } catch (error: any) {
      console.error('모듈 조회 오류:', error);
    } finally {
      setLoadingModules(false);
    }
  };

  // 변경 이력 조회
  const fetchChangeLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/change-logs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('변경 이력을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setChangeLogs(result.logs || []);
    } catch (error: any) {
      console.error('변경 이력 조회 오류:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // 기본 정보 편집 핸들러
  const handleEditChange = (field: string, value: string) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // 편집 취소
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditError(null);
    setEditData({
      name: organization.name || '',
      business_registration_number: organization.business_number || '',
      address: organization.address || '',
      phone: organization.phone || '',
      service_type: organization.service_type || '',
      owner_name: organization.owner_name || '',
      owner_email: organization.owner_email || '',
      owner_phone: organization.owner_phone || '',
    });
  };

  // 유효성 검증
  const validateEditData = (): string | null => {
    if (editData.business_registration_number) {
      const brn = editData.business_registration_number.replace(/-/g, '');
      if (!/^\d{10}$/.test(brn)) {
        return '사업자등록번호는 10자리 숫자여야 합니다.';
      }
    }
    if (editData.owner_email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(editData.owner_email)) {
        return '올바른 이메일 형식이 아닙니다.';
      }
    }
    return null;
  };

  // 기본 정보 저장
  const handleSaveEdit = async () => {
    const validationError = validateEditData();
    if (validationError) {
      setEditError(validationError);
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/info`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(editData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '정보 저장에 실패했습니다.');
      }

      const result = await response.json();

      if (result.changes && Object.keys(result.changes).length === 0) {
        alert('변경된 내용이 없습니다.');
      } else {
        alert(result.message || '사업자 정보가 수정되었습니다.');
        setIsEditMode(false);
        if (onUpdated) {
          onUpdated();
        }
      }
    } catch (err: any) {
      setEditError(err.message || '정보 저장 중 오류가 발생했습니다.');
    } finally {
      setEditLoading(false);
    }
  };

  // 변경 이력 액션 레이블
  const getActionLabel = (action: string): string => {
    const actionLabels: Record<string, string> = {
      'update_organization_info': '정보 수정',
      'approve_organization': '승인',
      'reject_organization': '거부',
      'suspend_organization': '정지',
      'reactivate_organization': '재활성화',
    };
    return actionLabels[action] || action;
  };

  // 탭 변경시 데이터 조회
  useEffect(() => {
    if (activeTab === 'subscription' && !dashboard) {
      fetchDashboard();
    }
    if (activeTab === 'modules' && !modules) {
      fetchModules();
    }
    if (activeTab === 'memos' && memos.length === 0) {
      fetchMemos();
    }
    if (activeTab === 'logs') {
      fetchChangeLogs();
    }
  }, [activeTab, dashboard, modules, memos.length, fetchMemos]);

  // 메모 렌더링 함수
  const renderMemo = (memo: Memo, isReply = false) => (
    <div
      key={memo.id}
      className={`${isReply ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''} ${
        memo.is_pinned && !isReply ? 'bg-yellow-50 border border-yellow-200' : 'bg-white border border-gray-200'
      } rounded-lg p-4 mb-3`}
    >
      {/* 메모 헤더 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {memo.is_pinned && !isReply && (
            <span className="text-yellow-600" title="고정된 메모">&#128204;</span>
          )}
          <span className="font-medium text-gray-900">{memo.author_name}</span>
          <span className="text-xs text-gray-500">{formatTime(memo.created_at)}</span>
          {memo.updated_at !== memo.created_at && (
            <span className="text-xs text-gray-400">(수정됨)</span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {!isReply && (
            <button
              onClick={() => handleTogglePin(memo.id)}
              className={`p-1 rounded hover:bg-gray-100 ${
                memo.is_pinned ? 'text-yellow-600' : 'text-gray-400'
              }`}
              title={memo.is_pinned ? '고정 해제' : '고정'}
            >
              &#128204;
            </button>
          )}
          <button
            onClick={() => {
              setEditingMemoId(memo.id);
              setEditingContent(memo.content);
            }}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded"
            title="수정"
          >
            &#9998;
          </button>
          <button
            onClick={() => handleDeleteMemo(memo.id)}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
            title="삭제"
          >
            &#128465;
          </button>
        </div>
      </div>

      {/* 메모 내용 */}
      {editingMemoId === memo.id ? (
        <div className="space-y-2">
          <textarea
            value={editingContent}
            onChange={(e) => setEditingContent(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            rows={3}
          />
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                setEditingMemoId(null);
                setEditingContent('');
              }}
              className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              onClick={() => handleUpdateMemo(memo.id)}
              disabled={savingMemo}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {savingMemo ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-gray-700 whitespace-pre-wrap text-sm">{memo.content}</p>
      )}

      {/* 답글 버튼 (상위 메모에만 표시) */}
      {!isReply && editingMemoId !== memo.id && (
        <div className="mt-3">
          {replyingToId === memo.id ? (
            <div className="space-y-2">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="답글을 입력하세요..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                rows={2}
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setReplyingToId(null);
                    setReplyContent('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={() => handleCreateReply(memo.id)}
                  disabled={savingMemo || !replyContent.trim()}
                  className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {savingMemo ? '저장 중...' : '답글 작성'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setReplyingToId(memo.id)}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              &#128172; 답글
            </button>
          )}
        </div>
      )}

      {/* 답글 목록 */}
      {memo.replies && memo.replies.length > 0 && (
        <div className="mt-4">
          {memo.replies.map((reply) => renderMemo(reply, true))}
        </div>
      )}
    </div>
  );

  // 모듈 토글
  const handleToggleModule = async (moduleId: string, currentEnabled: boolean) => {
    setUpdatingModule(moduleId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/modules/organizations/${organization.id}/${moduleId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_enabled: !currentEnabled }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '모듈 설정 변경에 실패했습니다.');
      }

      // 성공시 상태 업데이트
      setModules((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          optional: prev.optional.map((m) =>
            m.id === moduleId ? { ...m, is_enabled: !currentEnabled } : m
          ),
        };
      });
    } catch (error: any) {
      console.error('모듈 토글 오류:', error);
      alert(error.message);
    } finally {
      setUpdatingModule(null);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!organization.owner_email) {
      alert('오너 이메일이 없습니다.');
      return;
    }

    if (
      !window.confirm(
        `${organization.owner_email}로 비밀번호 재설정 메일을 발송하시겠습니까?`
      )
    ) {
      return;
    }

    setSending(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.owner_id}/password-reset`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '메일 발송에 실패했습니다.');
      }

      const result = await response.json();
      alert(result.message || '비밀번호 재설정 메일을 발송했습니다.');
    } catch (error: any) {
      console.error('비밀번호 재설정 메일 발송 오류:', error);
      alert(error.message || '메일 발송 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  // 사업자 삭제 핸들러
  const handleDelete = async () => {
    if (confirmName.trim() !== organization.name.trim()) {
      setDeleteError('사업자명이 일치하지 않습니다.');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ confirm_name: confirmName }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '삭제에 실패했습니다.');
      }

      // 성공
      alert(`'${organization.name}' 사업자가 삭제되었습니다.`);
      onClose();
      if (onDeleted) {
        onDeleted();
      }
    } catch (err: any) {
      setDeleteError(err.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 전화번호 저장 핸들러
  const handleSavePhone = async () => {
    if (!phoneValue.trim()) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    setPhoneLoading(true);
    setPhoneSuccess(false);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/phone`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone: phoneValue }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '전화번호 저장에 실패했습니다.');
      }

      setPhoneSuccess(true);
      setIsEditingPhone(false);
      setTimeout(() => setPhoneSuccess(false), 3000);
    } catch (err: any) {
      alert(err.message || '전화번호 저장 중 오류가 발생했습니다.');
    } finally {
      setPhoneLoading(false);
    }
  };

  // 할인율 저장 핸들러
  const handleSaveDiscount = async () => {
    setDiscountLoading(true);
    setDiscountSuccess(false);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/discount`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ discount_rate: discountRate }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '할인율 저장에 실패했습니다.');
      }

      setDiscountSuccess(true);
      setTimeout(() => setDiscountSuccess(false), 3000);
    } catch (err: any) {
      alert(err.message || '할인율 저장 중 오류가 발생했습니다.');
    } finally {
      setDiscountLoading(false);
    }
  };

  // 사용 중지 핸들러
  const handleSuspend = async (reason: string) => {
    setSuspendLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/suspend`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '사용 중지에 실패했습니다.');
      }

      alert(`'${organization.name}' 사업자가 사용 중지되었습니다.`);
      setShowSuspendModal(false);
      onClose();
      if (onUpdated) {
        onUpdated();
      }
    } catch (err: any) {
      alert(err.message || '사용 중지 처리 중 오류가 발생했습니다.');
    } finally {
      setSuspendLoading(false);
    }
  };

  // 재활성화 핸들러
  const handleReactivate = async () => {
    if (!window.confirm(`'${organization.name}' 사업자를 재활성화하시겠습니까?`)) {
      return;
    }

    setReactivateLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/owner-approvals/${organization.id}/reactivate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '재활성화에 실패했습니다.');
      }

      alert(`'${organization.name}' 사업자가 재활성화되었습니다.`);
      onClose();
      if (onUpdated) {
        onUpdated();
      }
    } catch (err: any) {
      alert(err.message || '재활성화 처리 중 오류가 발생했습니다.');
    } finally {
      setReactivateLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <h2 className="text-xl font-bold">🏢 {organization.name}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex flex-wrap">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'info'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              &#128203; 기본 정보
            </button>
            <button
              onClick={() => setActiveTab('memos')}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'memos'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              &#128221; 내부 메모
              {memos.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs">
                  {memos.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'subscription'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              &#128179; 구독/활동
            </button>
            <button
              onClick={() => setActiveTab('modules')}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'modules'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              &#129513; 모듈 관리
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'logs'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              &#128203; 변경 이력
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'settings'
                  ? 'border-b-2 border-red-600 text-red-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              &#9881; 설정
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'info' ? (
            <div className="space-y-6">
              {/* 편집 모드 토글 버튼 */}
              <div className="flex justify-end">
                {!isEditMode ? (
                  <button
                    onClick={() => setIsEditMode(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm flex items-center gap-2"
                  >
                    <span>편집</span>
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={editLoading}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 text-sm flex items-center gap-2"
                    >
                      {editLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>저장 중...</span>
                        </>
                      ) : (
                        <span>저장</span>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* 편집 에러 메시지 */}
              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {editError}
                </div>
              )}

              {/* 기본 정보 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                  기본 정보
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      조직 ID:
                    </span>
                    <span className="flex-1 text-gray-900 font-mono text-xs">
                      {organization.id}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      조직명:
                    </span>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) => handleEditChange('name', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900 font-semibold">
                        {organization.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      서비스 유형:
                    </span>
                    {isEditMode ? (
                      <select
                        value={editData.service_type}
                        onChange={(e) => handleEditChange('service_type', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="studyroom">독서실</option>
                        <option value="academy">학원</option>
                      </select>
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.service_type === 'studyroom' ? '독서실' :
                         organization.service_type === 'academy' ? '학원' :
                         organization.service_type}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 오너 정보 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                  👤 오너 정보
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      오너 ID:
                    </span>
                    <span className="flex-1 text-gray-900 font-mono text-xs">
                      {organization.owner_id}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      이름:
                    </span>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editData.owner_name}
                        onChange={(e) => handleEditChange('owner_name', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.owner_name || '-'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      이메일:
                    </span>
                    {isEditMode ? (
                      <input
                        type="email"
                        value={editData.owner_email}
                        onChange={(e) => handleEditChange('owner_email', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.owner_email || '-'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      연락처:
                    </span>
                    {isEditMode ? (
                      <input
                        type="tel"
                        value={editData.owner_phone}
                        onChange={(e) => handleEditChange('owner_phone', e.target.value)}
                        placeholder="010-0000-0000"
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.owner_phone || organization.phone || '-'}
                      </span>
                    )}
                  </div>
                  {/* 전화번호 편집 가능 (기존 방식 - 편집 모드가 아닐 때만 표시) */}
                  {!isEditMode && (
                  <div className="flex items-start">
                    <span className="w-32 text-gray-600 font-medium pt-1">
                      전화번호:
                    </span>
                    <div className="flex-1">
                      {isEditingPhone ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="tel"
                            value={phoneValue}
                            onChange={(e) => setPhoneValue(e.target.value)}
                            placeholder="010-0000-0000"
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                          />
                          <button
                            onClick={handleSavePhone}
                            disabled={phoneLoading}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
                          >
                            {phoneLoading ? '저장...' : '저장'}
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingPhone(false);
                              setPhoneValue(organization.phone || organization.owner_phone || '');
                            }}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className={`text-gray-900 ${!phoneValue ? 'text-red-500 italic' : ''}`}>
                            {phoneValue || '미등록 (알림톡 발송 불가)'}
                          </span>
                          <button
                            onClick={() => setIsEditingPhone(true)}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                          >
                            ✏️ 수정
                          </button>
                          {phoneSuccess && (
                            <span className="text-green-600 text-xs">저장됨</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* 사업자 정보 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                  사업자 정보
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      사업자번호:
                    </span>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editData.business_registration_number}
                        onChange={(e) => handleEditChange('business_registration_number', e.target.value)}
                        placeholder="000-00-00000"
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.business_number || '-'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">
                      전화번호:
                    </span>
                    {isEditMode ? (
                      <input
                        type="tel"
                        value={editData.phone}
                        onChange={(e) => handleEditChange('phone', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.phone || '-'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="w-32 text-gray-600 font-medium">주소:</span>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editData.address}
                        onChange={(e) => handleEditChange('address', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="flex-1 text-gray-900">
                        {organization.address || '-'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 승인 상태 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                  ✅ 승인 상태
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex">
                    <span className="w-32 text-gray-600 font-medium">
                      승인 여부:
                    </span>
                    <span className="flex-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          organization.is_approved
                            ? 'bg-green-100 text-green-800'
                            : organization.rejection_reason
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {organization.is_approved
                          ? '승인 완료'
                          : organization.rejection_reason
                          ? '승인 거부'
                          : '승인 대기'}
                      </span>
                    </span>
                  </div>
                  {organization.approval_date && (
                    <div className="flex">
                      <span className="w-32 text-gray-600 font-medium">
                        승인일:
                      </span>
                      <span className="flex-1 text-gray-900">
                        {new Date(organization.approval_date).toLocaleString(
                          'ko-KR'
                        )}
                      </span>
                    </div>
                  )}
                  {organization.approved_by && (
                    <div className="flex">
                      <span className="w-32 text-gray-600 font-medium">
                        승인자:
                      </span>
                      <span className="flex-1 text-gray-900">
                        {organization.approved_by}
                      </span>
                    </div>
                  )}
                  {organization.rejection_reason && (
                    <div className="flex">
                      <span className="w-32 text-gray-600 font-medium">
                        거부 사유:
                      </span>
                      <span className="flex-1 text-red-600">
                        {organization.rejection_reason}
                      </span>
                    </div>
                  )}
                  {/* 계정 상태 */}
                  <div className="flex">
                    <span className="w-32 text-gray-600 font-medium">
                      계정 상태:
                    </span>
                    <span className="flex-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          isOrganizationSuspended(organization)
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {isOrganizationSuspended(organization) ? '사용 중지' : '정상'}
                      </span>
                    </span>
                  </div>
                  {isOrganizationSuspended(organization) && organization.suspended_at && (
                    <div className="flex">
                      <span className="w-32 text-gray-600 font-medium">중지일:</span>
                      <span className="flex-1 text-gray-900">
                        {new Date(organization.suspended_at).toLocaleString('ko-KR')}
                      </span>
                    </div>
                  )}
                  {isOrganizationSuspended(organization) && organization.suspended_reason && (
                    <div className="flex">
                      <span className="w-32 text-gray-600 font-medium">중지 사유:</span>
                      <span className="flex-1 text-orange-600">{organization.suspended_reason}</span>
                    </div>
                  )}
                </div>
                {organization.is_approved && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {isOrganizationSuspended(organization) ? (
                      <button
                        onClick={handleReactivate}
                        disabled={reactivateLoading}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center"
                      >
                        {reactivateLoading ? '처리 중...' : '재활성화'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowSuspendModal(true)}
                        className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center"
                      >
                        사용 중지
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* 시스템 정보 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                  🕐 시스템 정보
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex">
                    <span className="w-32 text-gray-600 font-medium">
                      생성일:
                    </span>
                    <span className="flex-1 text-gray-900">
                      {new Date(organization.created_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-32 text-gray-600 font-medium">
                      수정일:
                    </span>
                    <span className="flex-1 text-gray-900">
                      {new Date(organization.updated_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'memos' ? (
            /* 내부 메모 탭 */
            <div className="space-y-4">
              {/* 메모 작성 폼 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">새 메모 작성</h3>
                <textarea
                  value={newMemoContent}
                  onChange={(e) => setNewMemoContent(e.target.value)}
                  placeholder="메모 내용을 입력하세요..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  rows={3}
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleCreateMemo}
                    disabled={savingMemo || !newMemoContent.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                  >
                    {savingMemo ? '저장 중...' : '메모 추가'}
                  </button>
                </div>
              </div>

              {/* 메모 목록 */}
              {loadingMemos ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : memos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>아직 작성된 메모가 없습니다.</p>
                  <p className="text-sm mt-1">위 폼을 사용하여 첫 메모를 작성해보세요.</p>
                </div>
              ) : (
                <div>
                  {memos.map((memo) => renderMemo(memo))}
                </div>
              )}
            </div>
          ) : activeTab === 'subscription' ? (
            /* 구독/활동 탭 */
            <div className="space-y-6">
              {loadingDashboard ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : dashboard ? (
                <>
                  {/* 현재 구독 상태 */}
                  <div className="border border-green-200 rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                      <h3 className="text-lg font-semibold text-green-800">
                        📦 현재 구독 상태
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm text-gray-500">플랜</span>
                          <p className="text-lg font-bold text-gray-900">
                            {dashboard.subscription.plan_name || 'FREE'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">상태</span>
                          <p>
                            <span className={`px-2 py-1 rounded text-sm font-medium ${
                              dashboard.subscription.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : dashboard.subscription.status === 'trial'
                                ? 'bg-blue-100 text-blue-800'
                                : dashboard.subscription.status === 'suspended'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {dashboard.subscription.status === 'active' ? '활성' :
                               dashboard.subscription.status === 'trial' ? '체험 중' :
                               dashboard.subscription.status === 'suspended' ? '정지됨' :
                               dashboard.subscription.status || '알 수 없음'}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* 구독 기간 */}
                      {dashboard.subscription.start_date && dashboard.subscription.end_date && (
                        <div className="mb-4">
                          <span className="text-sm text-gray-500">구독 기간</span>
                          <p className="text-sm text-gray-900">
                            {dashboard.subscription.start_date} ~ {dashboard.subscription.end_date}
                          </p>
                        </div>
                      )}

                      {/* 남은 일수 프로그레스 바 */}
                      {dashboard.subscription.days_remaining > 0 && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-500">남은 기간</span>
                            <span className="font-medium text-gray-900">
                              {dashboard.subscription.days_remaining}일
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full ${
                                dashboard.subscription.days_remaining > 30
                                  ? 'bg-green-500'
                                  : dashboard.subscription.days_remaining > 7
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{
                                width: `${Math.min(100, (dashboard.subscription.days_remaining / 365) * 100)}%`
                              }}
                            ></div>
                          </div>
                          {dashboard.subscription.days_remaining <= 30 && (
                            <p className="text-xs text-orange-600 mt-1">
                              ⚠️ 구독 만료가 {dashboard.subscription.days_remaining}일 남았습니다
                            </p>
                          )}
                        </div>
                      )}

                      {/* 결제 금액 */}
                      {dashboard.subscription.paid_amount > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <span className="text-sm text-gray-500">결제 금액</span>
                          <p className="text-lg font-bold text-gray-900">
                            {dashboard.subscription.paid_amount.toLocaleString()}원
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 최근 결제 내역 */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800">
                        💳 최근 결제 내역
                      </h3>
                    </div>
                    <div className="p-4">
                      {dashboard.recent_payments.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="pb-2">날짜</th>
                              <th className="pb-2">플랜</th>
                              <th className="pb-2">금액</th>
                              <th className="pb-2">상태</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboard.recent_payments.map((payment) => (
                              <tr key={payment.id} className="border-b last:border-b-0">
                                <td className="py-2 text-gray-900">
                                  {payment.date ? new Date(payment.date).toLocaleDateString('ko-KR') : '-'}
                                </td>
                                <td className="py-2 text-gray-900">{payment.plan_name || '-'}</td>
                                <td className="py-2 text-gray-900 font-medium">
                                  {payment.amount?.toLocaleString() || 0}원
                                </td>
                                <td className="py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    payment.status === 'active'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {payment.status === 'active' ? '활성' : payment.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-gray-500 text-center py-4">결제 내역이 없습니다</p>
                      )}
                    </div>
                  </div>

                  {/* 활성 사용자 수 */}
                  <div className="border border-blue-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                      <h3 className="text-lg font-semibold text-blue-800">
                        👥 활성 사용자 수
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-indigo-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-indigo-700">
                            {dashboard.user_counts.students}
                          </p>
                          <p className="text-sm text-gray-600">학생</p>
                        </div>
                        <div className="bg-pink-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-pink-700">
                            {dashboard.user_counts.parents}
                          </p>
                          <p className="text-sm text-gray-600">학부모</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-green-700">
                            {dashboard.user_counts.total}
                          </p>
                          <p className="text-sm text-gray-600">총 사용자</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 최근 활동 */}
                  <div className="border border-purple-200 rounded-lg overflow-hidden">
                    <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                      <h3 className="text-lg font-semibold text-purple-800">
                        📊 최근 활동
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">오너 마지막 로그인</span>
                          <span className="font-medium text-gray-900">
                            {dashboard.recent_activity.owner_last_login
                              ? new Date(dashboard.recent_activity.owner_last_login).toLocaleString('ko-KR')
                              : '기록 없음'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">오늘 출입 기록</span>
                          <span className="font-medium text-gray-900">
                            {dashboard.recent_activity.today_access_count}건
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">최근 7일 출입 기록</span>
                          <span className="font-medium text-gray-900">
                            {dashboard.recent_activity.week_access_count}건
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  대시보드 정보를 불러올 수 없습니다.
                </div>
              )}
            </div>
          ) : activeTab === 'modules' ? (
            /* 모듈 관리 탭 */
            <div className="space-y-6">
              {loadingModules ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : modules ? (
                <>
                  {/* Core 모듈 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                      🔒 기본 모듈 (항상 활성화)
                    </h3>
                    <div className="space-y-2">
                      {modules.core.map((module) => (
                        <div
                          key={module.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{module.icon}</span>
                            <div>
                              <div className="font-medium text-gray-900">
                                {module.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {module.description}
                              </div>
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            항상 ON
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optional 모듈 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                      🧩 선택 모듈 (ON/OFF 가능)
                    </h3>
                    <div className="space-y-2">
                      {modules.optional.map((module) => (
                        <div
                          key={module.id}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{module.icon}</span>
                            <div>
                              <div className="font-medium text-gray-900">
                                {module.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {module.description}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleToggleModule(module.id, module.is_enabled)
                            }
                            disabled={updatingModule === module.id}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              module.is_enabled
                                ? 'bg-indigo-600'
                                : 'bg-gray-300'
                            } ${
                              updatingModule === module.id
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                module.is_enabled
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  모듈 정보를 불러올 수 없습니다.
                </div>
              )}
            </div>
          ) : activeTab === 'logs' ? (
            /* 변경 이력 탭 */
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                변경 이력
              </h3>
              {loadingLogs ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : changeLogs.length > 0 ? (
                <div className="space-y-4">
                  {changeLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
                            {getActionLabel(log.action)}
                          </span>
                          <span className="ml-2 text-sm text-gray-600">
                            by {log.admin_name}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(log.created_at).toLocaleString('ko-KR')}
                        </span>
                      </div>
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <div className="space-y-2">
                          {Object.entries(log.changes).map(([field, change]) => (
                            <div key={field} className="text-sm">
                              <span className="font-medium text-gray-700">
                                {FIELD_LABELS[field] || field}:
                              </span>
                              <div className="ml-4 text-gray-600">
                                <span className="line-through text-red-500">
                                  {change.old || '(없음)'}
                                </span>
                                <span className="mx-2">-&gt;</span>
                                <span className="text-green-600 font-medium">
                                  {change.new || '(없음)'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {log.details && (!log.changes || Object.keys(log.changes).length === 0) && (
                        <p className="text-sm text-gray-600">{log.details}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  변경 이력이 없습니다.
                </div>
              )}
            </div>
          ) : (
            /* 설정 탭 */
            <div className="space-y-6">
              {/* 할인 설정 */}
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-800">
                    💰 할인 설정
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">기본 할인율</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      이 업체에 적용되는 기본 할인율입니다. 이용권 등록 시 자동으로 적용됩니다.
                    </p>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={discountRate}
                          onChange={(e) => {
                            const value = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                            setDiscountRate(value);
                          }}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-gray-600 font-medium">%</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        0 ~ 100 사이의 값을 입력하세요
                      </p>
                    </div>
                    <button
                      onClick={handleSaveDiscount}
                      disabled={discountLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {discountLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>저장 중...</span>
                        </>
                      ) : (
                        <span>저장</span>
                      )}
                    </button>
                  </div>

                  {discountSuccess && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                      ✅ 할인율이 저장되었습니다.
                    </div>
                  )}

                  {discountRate > 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-sm text-yellow-800">
                        <strong>예시:</strong> 월 290,000원 플랜 → 할인 적용 시{' '}
                        <strong className="text-green-700">
                          {Math.round(290000 * (100 - discountRate) / 100).toLocaleString()}원
                        </strong>
                        {' '}({discountRate}% 할인)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* 위험 구역 */}
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="bg-red-50 px-4 py-3 border-b border-red-200">
                  <h3 className="text-lg font-semibold text-red-800">
                    ⚠️ 위험 구역
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">사업자 삭제</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      사업자를 삭제하면 <span className="font-bold text-red-600">모든 데이터가 영구적으로 삭제</span>됩니다.
                      이 작업은 되돌릴 수 없습니다.
                    </p>
                    <ul className="text-sm text-gray-500 mb-4 list-disc list-inside space-y-1">
                      <li>회원 정보</li>
                      <li>이용권 정보</li>
                      <li>좌석 및 구역 정보</li>
                      <li>상품 정보</li>
                      <li>출입 기록</li>
                      <li>오너 계정</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-yellow-800 mb-2">
                      삭제를 확인하려면 아래에 사업자명을 정확히 입력하세요:
                    </p>
                    <p className="font-mono text-sm bg-yellow-100 px-2 py-1 rounded inline-block font-bold">
                      {organization.name}
                    </p>
                  </div>

                  <input
                    type="text"
                    value={confirmName}
                    onChange={(e) => {
                      setConfirmName(e.target.value);
                      setDeleteError(null);
                    }}
                    placeholder="사업자명을 입력하세요"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-3"
                  />

                  {deleteError && (
                    <p className="text-sm text-red-600 mb-3">{deleteError}</p>
                  )}

                  <button
                    onClick={handleDelete}
                    disabled={deleteLoading || confirmName.trim() !== organization.name.trim()}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2"
                  >
                    {deleteLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>삭제 중...</span>
                      </>
                    ) : (
                      <>
                        <span>🗑️</span>
                        <span>이 사업자 영구 삭제</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between items-center">
          {/* 비밀번호 재설정 버튼 - 정보 탭에서만 표시 */}
          {activeTab === 'info' && organization.owner_email ? (
            <button
              onClick={handleSendPasswordReset}
              disabled={sending}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {sending ? '발송 중...' : '비밀번호 재설정 메일 발송'}
            </button>
          ) : (
            <div></div>
          )}

          {/* 닫기 버튼 */}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            닫기
          </button>
        </div>
      </div>

      {/* Suspend Modal */}
      {showSuspendModal && (
        <SuspendOwnerModal
          organization={organization}
          onClose={() => setShowSuspendModal(false)}
          onConfirm={handleSuspend}
          loading={suspendLoading}
        />
      )}
    </div>
  );
};
